import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  XMarkIcon,
  BuildingStorefrontIcon,
  UserIcon,
  EnvelopeIcon,
  KeyIcon,
  PhoneIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { authService } from '../api/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ModalRegistroOpticaDemo = ({ isOpen, onClose, onRegistroExitoso, motivo = '' }) => {
  const [formData, setFormData] = useState({
    nombre_optica: '',
    nombre_completo: '',
    email: '',
    contraseña: '',
    confirmar_contraseña: '',
    telefono: ''
  });
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [cargando, setCargando] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre_optica.trim()) {
      Swal.fire({ icon: 'warning', title: 'Nombre de óptica requerido', text: 'Por favor, ingresá el nombre de tu óptica comercial.' });
      return;
    }
    if (!formData.nombre_completo.trim()) {
      Swal.fire({ icon: 'warning', title: 'Nombre requerido', text: 'Por favor, ingresá tu nombre completo.' });
      return;
    }
    if (!formData.email.trim()) {
      Swal.fire({ icon: 'warning', title: 'Email requerido', text: 'Ingresá un correo electrónico válido.' });
      return;
    }
    if (formData.contraseña.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Contraseña muy corta', text: 'La contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (formData.contraseña !== formData.confirmar_contraseña) {
      Swal.fire({
        icon: 'warning',
        title: 'Las contraseñas no coinciden',
        text: 'Por favor, verificá que ambas contraseñas coincidan para no tener problemas de acceso en el futuro.'
      });
      return;
    }

    try {
      setCargando(true);
      const res = await fetch(`${API_URL}/api/auth/registro-optica-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombre_optica: formData.nombre_optica.trim(),
          nombre_completo: formData.nombre_completo.trim(),
          email: formData.email.trim().toLowerCase(),
          contraseña: formData.contraseña,
          telefono: formData.telefono ? formData.telefono.trim() : null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Ocurrió un error al crear la óptica.');
      }

      // Guardar sesión del nuevo usuario admin de su propia óptica
      if (data.access_token && data.refresh_token && data.usuario) {
        authService.guardarTokens(data.access_token, data.refresh_token, data.usuario);
      }

      await Swal.fire({
        icon: 'success',
        title: '¡Felicitaciones! Óptica Creada',
        html: `
          <div class="space-y-2 text-sm text-slate-700 text-left">
            <p>Se ha creado exitosamente <strong>${formData.nombre_optica}</strong> con <strong>3 meses de acceso completo bonificado</strong>.</p>
            <p class="text-xs text-slate-500">Ya podés empezar a registrar pacientes, graduaciones, stock y ventas en tu propio espacio privado.</p>
          </div>
        `,
        confirmButtonText: 'Entrar a mi nueva Óptica',
        confirmButtonColor: '#0284c7'
      });

      if (onRegistroExitoso) {
        onRegistroExitoso(data.usuario);
      }
      onClose();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo crear la óptica',
        text: err.message || 'Error de conexión con el servidor.'
      });
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-3 sm:p-5 overflow-y-auto bg-slate-950/85 backdrop-blur-md animate-fadeIn font-tour">
      <div className="relative w-full max-w-xl my-auto bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden">
        {/* Header con gradiente azul vibrante y tipografía amplia */}
        <div className="relative bg-gradient-to-r from-[#0060c4] via-[#0072db] to-[#008bf8] p-6 sm:p-8 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
            title="Cerrar"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-white/20 text-white text-xs sm:text-sm font-medium uppercase tracking-wider mb-2 border border-white/30">
            <SparklesIcon className="w-4 h-4 text-cyan-200" />
            Acceso Completo Bonificado
          </div>

          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-white mt-1 leading-tight">
            Comienza con tu propia Óptica
          </h2>

          <p className="text-sm sm:text-base text-blue-50/95 mt-2 leading-relaxed font-normal">
            {motivo ? `Para ${motivo}, ` : 'Para comenzar a gestionar tu negocio, '}
            registra tu óptica hoy y disfruta de <strong className="font-semibold text-white underline decoration-white/40 underline-offset-2">3 meses de prueba gratis</strong> sin tarjeta de crédito.
          </p>

          {/* Badge 3 meses */}
          <div className="mt-4 flex items-center gap-3.5 bg-white/15 backdrop-blur-md rounded-2xl p-3.5 sm:p-4 border border-white/20">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0 border border-white/30">
              <RocketLaunchIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-xs sm:text-sm">
              <div className="font-medium text-white text-sm sm:text-base">Plan Profesional 100% Bonificado</div>
              <div className="text-blue-100 font-normal mt-0.5">Pacientes, Recetas, AFIP, Presupuestos y Tienda Online activa por 90 días.</div>
            </div>
          </div>
        </div>

        {/* Formulario con campos más visibles y cómodos */}
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-4">
          {/* Nombre de la óptica */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-800 uppercase tracking-wide mb-1.5">
              Nombre de tu Óptica <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <BuildingStorefrontIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                name="nombre_optica"
                value={formData.nombre_optica}
                onChange={handleChange}
                placeholder="Ej: Óptica Visión Moderna"
                required
                className="w-full pl-11 pr-4 py-3 text-sm sm:text-base bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all font-normal"
              />
            </div>
          </div>

          {/* Nombre completo del responsable */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-800 uppercase tracking-wide mb-1.5">
              Tu Nombre Completo <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <UserIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                name="nombre_completo"
                value={formData.nombre_completo}
                onChange={handleChange}
                placeholder="Ej: Laura Martínez"
                required
                className="w-full pl-11 pr-4 py-3 text-sm sm:text-base bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all font-normal"
              />
            </div>
          </div>

          {/* Email y Teléfono WhatsApp */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-800 uppercase tracking-wide mb-1.5">
                Email de Acceso <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <EnvelopeIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="laura@mioptica.com"
                  required
                  className="w-full pl-11 pr-3 py-3 text-sm sm:text-base bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all font-normal"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-800 uppercase tracking-wide mb-1.5">
                Teléfono WhatsApp <span className="text-slate-400 font-normal lowercase">(opcional)</span>
              </label>
              <div className="relative">
                <PhoneIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="Ej: +54 9 11 2233 4455"
                  className="w-full pl-11 pr-3 py-3 text-sm sm:text-base bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all font-normal"
                />
              </div>
            </div>
          </div>

          {/* Contraseña y Confirmar Contraseña */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-800 uppercase tracking-wide mb-1.5">
                Contraseña <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <KeyIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={mostrarContrasena ? 'text' : 'password'}
                  name="contraseña"
                  value={formData.contraseña}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  className="w-full pl-11 pr-11 py-3 text-sm sm:text-base bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all font-normal"
                />
                <button
                  type="button"
                  onClick={() => setMostrarContrasena(!mostrarContrasena)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded-lg transition-colors"
                  tabIndex={-1}
                  title={mostrarContrasena ? 'Ocultar contraseña' : 'Ver contraseña'}
                >
                  {mostrarContrasena ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-slate-800 uppercase tracking-wide mb-1.5">
                Confirmar Contraseña <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <KeyIcon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={mostrarConfirmar ? 'text' : 'password'}
                  name="confirmar_contraseña"
                  value={formData.confirmar_contraseña}
                  onChange={handleChange}
                  placeholder="Repetí tu contraseña"
                  required
                  minLength={6}
                  className={`w-full pl-11 pr-11 py-3 text-sm sm:text-base bg-slate-50 border rounded-xl focus:bg-white focus:ring-2 outline-none transition-all font-normal ${
                    formData.confirmar_contraseña
                      ? formData.contraseña === formData.confirmar_contraseña
                        ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-500/20'
                        : 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20'
                      : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded-lg transition-colors"
                  tabIndex={-1}
                  title={mostrarConfirmar ? 'Ocultar contraseña' : 'Ver contraseña'}
                >
                  {mostrarConfirmar ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              {formData.confirmar_contraseña && formData.contraseña !== formData.confirmar_contraseña && (
                <p className="text-xs text-rose-600 mt-1 font-medium">Las contraseñas no coinciden</p>
              )}
              {formData.confirmar_contraseña && formData.contraseña === formData.confirmar_contraseña && (
                <p className="text-xs text-emerald-600 mt-1 font-medium">¡Las contraseñas coinciden!</p>
              )}
            </div>
          </div>

          {/* Ventajas checklist con viñetas estilizadas */}
          <div className="bg-blue-50/40 rounded-2xl p-4 border border-blue-100/90 space-y-2">
            <div className="flex items-center gap-2.5 text-xs sm:text-sm font-medium text-slate-700">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0" />
              <span>3 meses de prueba completa sin compromiso</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs sm:text-sm font-medium text-slate-700">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0" />
              <span>Tus datos son 100% privados y aislados bajo tu óptica</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs sm:text-sm font-medium text-slate-700">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0" />
              <span>Acceso inmediato apenas envías este formulario</span>
            </div>
          </div>

          {/* Botones de acción agrandados */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={cargando}
              className="px-5 py-3 text-sm sm:text-base font-normal text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-50"
            >
              Seguir en Modo Demo
            </button>

            <button
              type="submit"
              disabled={cargando}
              className="inline-flex items-center gap-2 px-6 sm:px-7 py-3 text-sm sm:text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-all disabled:opacity-60 cursor-pointer"
            >
              <RocketLaunchIcon className="w-5 h-5" />
              {cargando ? 'Creando tu Óptica...' : 'Activar mis 3 Meses Gratis'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ModalRegistroOpticaDemo.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRegistroExitoso: PropTypes.func,
  motivo: PropTypes.string
};

export default ModalRegistroOpticaDemo;
